import React, { useEffect, useRef } from 'react';
import { STATUS_LABELS } from '../MyDay/orderStatus';
import { MIN_QUERY_LENGTH } from './useGlobalSearch';
import Icon from '../common/Icon';

const joinParts = (...parts) => parts.filter(Boolean).join(' · ');

export const buildGroups = (results) => {
    if (!results) return [];
    const groups = [
        {
            title: 'Клиенты',
            icon: 'user',
            items: (results.clients || []).map((client) => ({
                key: `client-${client.id}`,
                to: `/clients/${client.id}`,
                title: client.full_name,
                subtitle: client.phone,
            })),
        },
        {
            title: 'Автомобили',
            icon: 'car',
            items: (results.vehicles || []).map((vehicle) => ({
                key: `vehicle-${vehicle.id}`,
                to: `/vehicles/${vehicle.id}`,
                title: `${vehicle.brand} ${vehicle.model}`,
                subtitle: joinParts(vehicle.license_plate, vehicle.client_name),
            })),
        },
        {
            title: 'Заказы',
            icon: 'orders',
            items: (results.orders || []).map((order) => ({
                key: `order-${order.id}`,
                to: `/orders/${order.id}`,
                title: `Заказ #${order.id}`,
                subtitle: joinParts(
                    STATUS_LABELS[order.status] || order.status,
                    order.vehicle_label,
                    order.license_plate,
                    order.client_name
                ),
            })),
        },
    ];
    return groups.filter((group) => group.items.length > 0);
};

export const flattenGroups = (groups) => groups.flatMap((group) => group.items);

export const getOptionId = (listId, item) => `${listId}-${item.key}`;

const SearchResults = ({ listId, query, search, groups, activeIndex, onHover, onSelect }) => {
    const activeRef = useRef(null);
    const trimmedLength = query.trim().length;

    useEffect(() => {
        activeRef.current?.scrollIntoView?.({ block: 'nearest' });
    }, [activeIndex]);

    if (trimmedLength < MIN_QUERY_LENGTH) {
        return (
            <div className="global-search-message">
                {trimmedLength === 0
                    ? 'Клиент, телефон, автомобиль, госномер, VIN или номер заказа'
                    : `Введите минимум ${MIN_QUERY_LENGTH} символа`}
            </div>
        );
    }

    if (search.status === 'error') {
        return (
            <div className="global-search-message is-error" role="alert">
                <p>{search.error}</p>
                <button type="button" className="btn btn-secondary" onClick={search.retry}>Повторить</button>
            </div>
        );
    }

    if (!groups.length) {
        return (
            <div className="global-search-message" role="status">
                {search.status === 'ready' ? 'Ничего не найдено' : 'Поиск...'}
            </div>
        );
    }

    let optionIndex = -1;
    return (
        <div id={listId} role="listbox" aria-label="Результаты поиска" className="global-search-list">
            {groups.map((group) => (
                <div key={group.title} role="group" aria-label={group.title}>
                    <div className="global-search-group-title" aria-hidden="true">{group.title}</div>
                    {group.items.map((item) => {
                        optionIndex += 1;
                        const index = optionIndex;
                        const isActive = index === activeIndex;
                        return (
                            <div
                                key={item.key}
                                id={getOptionId(listId, item)}
                                ref={isActive ? activeRef : null}
                                role="option"
                                aria-selected={isActive}
                                className={`global-search-option${isActive ? ' active' : ''}`}
                                onMouseEnter={() => onHover(index)}
                                onClick={() => onSelect(item)}
                            >
                                <Icon name={group.icon} size={18} className="global-search-option-icon" />
                                <div className="global-search-option-text">
                                    <div className="global-search-option-title">{item.title}</div>
                                    {item.subtitle && (
                                        <div className="global-search-option-subtitle">{item.subtitle}</div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ))}
        </div>
    );
};

export default SearchResults;
